/**
 * ResearchPage Component
 * 
 * Built with shadcn components.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Search, Loader2, RefreshCw, ExternalLink, AlertCircle } from 'lucide-react'
import { Button } from '../../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import { Input } from '../../components/ui/input'
import { Alert, AlertDescription } from '../../components/ui/alert'
import { ApiServices } from '../../services/ApiServices'
import { REGIONS } from '../../constants/regions'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from '../../components/ui/select'

function escapeHtml(text) {
  if (!text) return ''
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

// Parse a single line of pipe-separated cells (with or without leading/trailing |)
function parseTableRow(line) {
  const trimmed = line.trim()
  if (!trimmed.includes('|')) return null
  const withoutOuter = trimmed.startsWith('|')
    ? trimmed.slice(1, trimmed.endsWith('|') ? -1 : undefined).trim()
    : trimmed
  const cells = withoutOuter.split('|').map(cell => cell.trim())
  return cells.length > 0 ? cells : null
}

// Check if line is a table separator (|---|---| or |:---| or just dashes)
function isTableSeparatorLine(line) {
  const t = line.trim()
  if (/^\|[\s\-:]+\|$/.test(t)) return true
  // Lines consisting only of pipes, dashes, colons and whitespace (including variants like "- --- --- :--")
  if (/^[\s\|\-:]+$/.test(t)) return true
  return false
}

// Convert markdown table block (header + optional separator lines + body rows) to HTML table
function convertTableBlock(lines, startIndex) {
  const rows = []
  let i = startIndex
  while (i < lines.length) {
    const line = lines[i]
    const cells = parseTableRow(line)
    if (cells) {
      if (!isTableSeparatorLine(line)) rows.push(cells)
      i += 1
      continue
    }
    if (isTableSeparatorLine(line)) {
      i += 1
      continue
    }
    break
  }
  if (rows.length === 0) return { html: '', nextIndex: startIndex + 1 }
  const thead = rows[0].map(c => `<th class="border border-border px-3 py-2 text-left text-sm font-semibold bg-muted/50">${escapeHtml(c)}</th>`).join('')
  const bodyRows = rows.slice(1).map(row =>
    row.map(c => `<td class="border border-border px-3 py-2 text-sm">${escapeHtml(c)}</td>`).join('')
  ).map(rowHtml => `<tr>${rowHtml}</tr>`).join('')
  const tableHtml = `<div class="my-4 overflow-x-auto"><table class="w-full border-collapse border border-border text-left"><thead><tr>${thead}</tr></thead><tbody>${bodyRows}</tbody></table></div>`
  return { html: tableHtml, nextIndex: i }
}

// Markdown to HTML converter (improved, with proper table support)
function convertMarkdownToHtml(markdown) {
  if (!markdown) return ""

  const lines = markdown.split('\n')
  const output = []
  let i = 0

  while (i < lines.length) {
    const line = lines[i]
    const trimmed = line.trim()

    // Table: line with pipe-separated cells (GFM-style or LLM-style)
    if (parseTableRow(line) && !isTableSeparatorLine(line)) {
      const { html: tableHtml, nextIndex } = convertTableBlock(lines, i)
      if (tableHtml) {
        output.push(tableHtml)
        i = nextIndex
        continue
      }
    }

    // Standalone separator lines (only dashes/pipes/colons) — remove, do not show
    if (/^[\s\|\-:]+$/.test(trimmed)) {
      i += 1
      continue
    }

    output.push(line)
    i += 1
  }

  let html = output.join('\n')

  // Headers
  html = html
    .replace(/^#### (.*?)$/gm, '<h5 class="text-base font-semibold mt-4 mb-2">$1</h5>')
    .replace(/^### (.*?)$/gm, '<h4 class="text-lg font-semibold mt-4 mb-2">$1</h4>')
    .replace(/^## (.*?)$/gm, '<h3 class="text-xl font-bold mt-6 mb-3">$1</h3>')
    .replace(/^# (.*?)$/gm, '<h2 class="text-2xl font-bold mt-6 mb-4">$1</h2>')
    // Bold and italic
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    // Links
    .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-primary hover:underline">$1</a>')
    // Lists
    .replace(/^\* (.*?)$/gm, '<li>$1</li>')
    .replace(/^- (.*?)$/gm, '<li>$1</li>')
    .replace(/^\d+\. (.*?)$/gm, '<li>$1</li>')

  // Paragraphs
  html = html
    .split(/\n\n+/)
    .map(p => p.trim())
    .filter(p => p && p !== '---' && !/^[\s\|\-:]+$/.test(p))
    .map(p => {
      if (p.startsWith('<h') || p.startsWith('<li') || p.startsWith('<ul') || p.startsWith('<ol') || p.startsWith('<hr') || p.startsWith('<div')) {
        return p
      }
      return `<p class="mb-3">${p.replace(/\n/g, '<br>')}</p>`
    })
    .join('')

  // Wrap consecutive list items
  html = html.replace(/(<li>.*?<\/li>\s*)+/g, '<ul class="list-disc pl-6 mb-4 space-y-1">$&</ul>')

  // Clean up any remaining raw table separator artifacts (inside paragraphs)
  html = html.replace(/\|[\s:|-]{2,}/g, '')
  html = html.replace(/:---/g, '')
  html = html.replace(/---:/g, '')

  return html
}

const ResearchPage = () => {
  const [query, setQuery] = useState('')
  const [selectedGeo, setSelectedGeo] = useState('all') // 'all' = Worldwide / no specific geo
  const [currentTaskId, setCurrentTaskId] = useState(null)
  const [status, setStatus] = useState('idle')
  const [statusMessage, setStatusMessage] = useState('Ready to research...')
  const [resultsContent, setResultsContent] = useState('')
  const [sources, setSources] = useState([])
  const [errorMessage, setErrorMessage] = useState('')

  const pollingIntervalRef = useRef(null)
  const location = useLocation()
  const navigate = useNavigate()

  const clearPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }
  }, [])

  const displayResults = useCallback((data) => {
    setStatus('completed')
    if (!data?.content) {
      setResultsContent('<p class="text-muted-foreground">Research results unavailable</p>')
      setSources([])
      return
    }
    setResultsContent(convertMarkdownToHtml(data.content))
    setSources(data.citations || [])
  }, [])

  const pollResearchStatus = useCallback((taskId) => {
    if (!taskId) return
    clearPolling()
    setStatus('polling')

    pollingIntervalRef.current = setInterval(() => {
      ApiServices.getResearchStatus(taskId)
        .then((data) => {
          setStatusMessage(data.progress_message || 'Research in progress...')

          switch (data.status) {
            case 'in_progress':
            case 'pending':
              break
            case 'completed':
              clearPolling()
              setStatusMessage('Research completed')
              ApiServices.getResearchResults(taskId)
                .then(resultData => displayResults(resultData))
                .catch(err => {
                  console.error("Error fetching results:", err)
                  setErrorMessage('Error loading results')
                  setStatus('error')
                })
              break
            case 'error':
              clearPolling()
              setErrorMessage(data.error || 'An error occurred during research')
              setStatus('error')
              break
            default:
              setStatusMessage('Checking research status...')
          }
        })
        .catch(() => {
          // Treat transient polling errors as temporary and keep the interval running
          setStatusMessage('Временная ошибка связи. Пробуем еще раз...')
        })
    }, 2000)
  }, [clearPolling, displayResults])

  const handleFormSubmit = useCallback((e) => {
    e.preventDefault()
    const queryValue = query.trim()
    if (!queryValue) return

    setResultsContent('')
    setSources([])
    setErrorMessage('')
    setStatus('loading')
    setStatusMessage('Starting research...')
    clearPolling()
    const shouldSendGeo = selectedGeo && selectedGeo !== 'all'
    const geoQs = shouldSendGeo ? `&geo=${encodeURIComponent(selectedGeo)}` : ''
    navigate(`${location.pathname}?query=${encodeURIComponent(queryValue)}${geoQs}`, { replace: true })

    const startOptions = shouldSendGeo ? { geo: selectedGeo } : {}
    ApiServices.startResearch(queryValue, startOptions)
      .then((data) => {
        if (data.from_cache && data.content) {
          displayResults({ content: data.content, citations: data.citations || [] })
          setStatus('completed')
          navigate(`${location.pathname}?query=${encodeURIComponent(queryValue)}${geoQs}`, { replace: true, state: null })
        } else if (data.task_id) {
          setCurrentTaskId(data.task_id)
          setStatus('polling')
          setStatusMessage('Fetching research results...')
          pollResearchStatus(data.task_id)
          navigate(`${location.pathname}?query=${encodeURIComponent(queryValue)}&task_id=${data.task_id}${geoQs}`, { replace: true })
        } else {
          throw new Error(data.error || 'Failed to start research')
        }
      })
      .catch((error) => {
        console.error("Error starting research:", error)
        setErrorMessage(error.message || 'An error occurred while starting research')
        setStatus('error')
      })
  }, [query, selectedGeo, clearPolling, pollResearchStatus, displayResults, navigate, location.pathname])

  useEffect(() => {
    if (currentTaskId && (status === 'loading' || status === 'polling')) {
      pollResearchStatus(currentTaskId)
    }
    return () => clearPolling()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTaskId])

  useEffect(() => {
    // Handle cached research from navigation state (from TrendDetailPage)
    const state = location.state
    if (state?.fromCache && state.content) {
      setQuery(state.query || '')
      setSelectedGeo(state.geo || 'all')
      displayResults({ content: state.content, citations: state.citations || [] })
      return () => clearPolling()
    }

    const params = new URLSearchParams(location.search)
    const urlQuery = params.get('query')
    const urlTaskId = params.get('task_id')
    const urlGeo = params.get('geo')

    if (urlTaskId) {
      setQuery(urlQuery || '')
      setSelectedGeo(urlGeo || 'all')
      setCurrentTaskId(urlTaskId)
      setStatus('loading')
      setStatusMessage('Fetching research results...')
      pollResearchStatus(urlTaskId)
    } else if (urlQuery && !currentTaskId) {
      setQuery(urlQuery)
      setSelectedGeo(urlGeo || 'all')
    }

    return () => clearPolling()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.state, clearPolling, pollResearchStatus, displayResults])

  useEffect(() => {
    ApiServices.trackAuditEvent('page_view', 'research');
  }, []);

  const handleReset = () => {
    clearPolling()
    setQuery('')
    setSelectedGeo('all')
    setCurrentTaskId(null)
    setStatus('idle')
    setStatusMessage('Ready to research...')
    setResultsContent('')
    setSources([])
    setErrorMessage('')
    navigate(location.pathname, { replace: true })
  }

  const showLoading = status === 'loading' || status === 'polling'
  const showResults = status === 'completed' && resultsContent
  const showError = status === 'error'
  const showSources = showResults && sources.length > 0

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h1 className="text-3xl font-bold tracking-tight mb-2">Trend Research</h1>
        <p className="text-muted-foreground text-lg">
          AI-powered deep research on any topic
        </p>
      </div>

      <div className="max-w-xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Enter a topic to research</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleFormSubmit} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Контекст региона (необязательно)</label>
                <Select value={selectedGeo} onValueChange={setSelectedGeo} disabled={showLoading}>
                  <SelectTrigger>
                    <SelectValue placeholder="Worldwide" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Worldwide</SelectItem>
                    {REGIONS.map((group) => (
                      <SelectGroup key={group.label}>
                        <SelectLabel>{group.label}</SelectLabel>
                        {group.countries.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            {c.code} - {c.name}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="e.g., Real estate investments"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  disabled={showLoading}
                  className="pl-10"
                  required
                />
              </div>
              <div className="flex gap-2 justify-center">
                <Button type="submit" disabled={showLoading || !query.trim()}>
                  {showLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Researching...
                    </>
                  ) : (
                    'Research'
                  )}
                </Button>
                {(currentTaskId || showError) && (
                  <Button type="button" variant="outline" onClick={handleReset}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Start Over
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Loading */}
      {showLoading && (
        <Card>
          <CardContent className="py-12 text-center">
            <Loader2 className="h-8 w-8 mx-auto animate-spin text-primary mb-4" />
            <p className="text-lg font-medium">{statusMessage}</p>
          </CardContent>
        </Card>
      )}

      {/* Error */}
      {showError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}

      {/* Results */}
      {showResults && (
        <div className="max-w-4xl mx-auto w-full">
          <Card>
            <CardContent className="py-8">
              <div
                className="prose prose-sm md:prose-base max-w-none dark:prose-invert"
                dangerouslySetInnerHTML={{ __html: resultsContent }}
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Sources */}
      {showSources && (
        <div className="max-w-4xl mx-auto w-full">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Sources</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-4">
                {sources.map((citation, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-muted-foreground font-mono text-sm">{index + 1}.</span>
                    {typeof citation === 'string' ? (
                      <span>{citation}</span>
                    ) : (
                      <div>
                        <a
                          href={citation.url || '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline inline-flex items-center gap-1"
                        >
                          {citation.title || 'Source'}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                        {citation.snippet && (
                          <p className="text-sm text-muted-foreground mt-1">{citation.snippet}</p>
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}

export default ResearchPage